import { useMemo } from 'react'
import cn from 'classnames/bind'
import { isEmpty } from 'ramda'

import EmptyWarn from 'app/common/layouts/EmptyWarn'
import Loading from 'app/common/components/Loader'
import { BannerContent } from 'app/common/components-next/banner'
import Person from 'app/common/classes/person'
import Sticky from 'app/common/templates-next/sticky'
import { PageTemplateContent } from 'app/common/templates-next/page-template'

import {
  Table,
  CheckStateProvider,
  useCopyColumnsWidths,
  useTopShiftState,
  fancyRowRootClassName,
} from 'app/common/ui-components/table'

import type { Status } from '../../dataTypes/orderRequests/statuses'
import type { Params } from '../../dataTypes/orderRequests/OrderRequest.types'
import useOrderRequests from './useOrderRequests'
import useItemsFiltered from './useItemsFiltered'
import useEmptyWarn from './useEmptyWarn'
import { useTitles, useColumns } from './columns'
import TableTHead from './TableTHead'
import TableRow from './TableRow'

import styles from './Content.module.scss';

const cx = cn.bind(styles)

type Props = {
  status: Status
  params: Params
  editable?: boolean
  customer: Person
}

const OrderRequestsContent = ({ status, params, editable, customer }: Props) => {
  const [rawItems, loading, orderRequestsRequest] = useOrderRequests(params)
  const items = useItemsFiltered(rawItems, status)
  const empty = isEmpty(items)

  const displayTable = !empty && !loading
  const displayEmptyWarn = empty && !loading
  const displayLoading = loading

  const emptyWarn = useEmptyWarn(status)

  const titles = useTitles()
  const columns = useColumns(editable)

  const updateKey = useMemo(() => [], [items, editable]) // eslint-disable-line react-hooks/exhaustive-deps
  const [refFrom, refTo] = useCopyColumnsWidths(updateKey)

  const [updateRowNode, calcTopShift, nodes] = useTopShiftState()

  return (
    <>
      <Sticky>
        {(ref, sticky, shadow) => (
          <PageTemplateContent
            reference={ref}
            shadowBottom={shadow}
            className={cx({ sticky })}
            skipPadding
            flex
          >
            <div className={styles.tableContainer}>
              <Table className={styles.table}>
                <thead>
                  <TableTHead reference={refTo} titles={titles} columns={columns} />
                </thead>
              </Table>
            </div>

            <BannerContent
              placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
              className={styles.bannerPlaceholder}
              displayAsPlaceholder
              height={0}
            />
          </PageTemplateContent>
        )}
      </Sticky>

      <PageTemplateContent className={styles.layout} skipPadding flex>
        <div className={styles.tableContainer}>
          {displayLoading && <Loading />}

          {displayEmptyWarn && <EmptyWarn>{emptyWarn}</EmptyWarn>}

          {displayTable && (
            <Table className={cx(styles.table, fancyRowRootClassName)}>
              <thead>
                <TableTHead reference={refFrom} titles={titles} columns={columns} hidden dummy />
              </thead>
              <tbody className={styles.rowsContainer}>
                <CheckStateProvider resetKey={updateKey}>
                  {items.map((item, index) => (
                    <TableRow
                      key={item.id}
                      index={index}
                      customerId={params.customerId}
                      disabled={loading}
                      data={item}
                      columns={columns}
                      updateRowNode={updateRowNode}
                      calcTopShift={calcTopShift}
                      trElem={nodes[String(index)]}
                      customer={customer}
                      onUpdate={orderRequestsRequest}
                      editable={editable}
                    />
                  ))}
                </CheckStateProvider>
              </tbody>
            </Table>
          )}
        </div>

        <BannerContent
          // leaveContainerOnEmpty
          refreshOnMount
          className={styles.bannerContent}
          placeId="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
        />
      </PageTemplateContent>
    </>
  )
}

export default OrderRequestsContent
